import React, { useMemo } from 'react';
import { merge } from 'lodash';
import { Core } from '@balance-web/core';
import type { CoreProps, LinkComponent } from '@balance-web/core';
import type { BalanceTheme, BalanceThemeRaw } from '@balance-web/theme';
import type { DeepPartial } from '@balance-web/utils';
import { cloneDeep } from 'lodash';

import {
  lightTheme,
  lightThemeCSSVars,
  lightThemeRaw,
} from '@reckon-web/reckon-balance-theme';

// This is the one place a whole theme object crosses into the design system.
// UNDO THIS when csstype patch solution is released.
// @ts-ignore — `lightTheme.elevation.card` is a string where the type says
// number, and the reason the annotation cannot simply be dropped: without it the
// mismatch resurfaces at `<Core theme={mergedTheme}>` with nothing suppressing it.
const customTheme: BalanceTheme = {
  ...lightTheme,
  name: 'Reckon light',
};

export type ThemeProviderProps = {
  themeOverrides?: DeepPartial<BalanceTheme>;
  linkComponent: LinkComponent;
} & Pick<
  CoreProps,
  | 'globalStyles'
  | 'portalParent'
  | 'portalParentElement'
  | 'fastMode'
  | 'children'
  | 'appType'
>;

function toCSSVars(obj: Record<string, any>, parentKey = '') {
  let vars: Record<string, string | number> = {};

  Object.keys(obj).forEach((key) => {
    let fullKey = parentKey ? `${parentKey}-${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(vars, toCSSVars(obj[key], fullKey));
    } else {
      vars[`--${fullKey}`] = obj[key];
    }
  });

  return vars;
}

export const ThemeProvider = ({
  themeOverrides,
  linkComponent,
  globalStyles,
  fastMode,
  portalParent,
  portalParentElement,
  children,
  appType,
}: ThemeProviderProps) => {
  const originThemeCopy = useMemo(() => {
    return cloneDeep(customTheme);
  }, []);
  const mergedTheme = useMemo(() => {
    return merge(originThemeCopy, themeOverrides);
  }, [originThemeCopy, themeOverrides]);

  const themeCSSVars = useMemo(() => {
    const cssVarsOverrides = toCSSVars(themeOverrides || {});

    return merge(lightThemeCSSVars, cssVarsOverrides);
  }, [themeOverrides]);

  const mergedThemeRaw = useMemo(() => {
    return merge(lightThemeRaw, (themeOverrides as unknown) as BalanceThemeRaw);
  }, [themeOverrides]);

  return (
    <Core
      appType={appType}
      fastMode={fastMode}
      globalStyles={globalStyles}
      linkComponent={linkComponent}
      portalParent={portalParent}
      portalParentElement={portalParentElement}
      theme={mergedTheme}
      themeCSSVars={themeCSSVars}
      themeRaw={mergedThemeRaw}
    >
      {children}
    </Core>
  );
};
